import React from 'react'
import { Box, ListItemText, Menu, MenuItem } from '@mui/material'
import IconButton from './IconButton'
import { useMenuActions } from '../utils/useMenuActions'
import { Restaurant } from '../utils/restaurant'

const sxReport = { ml: '1rem' } as const

type SourceMenu = NonNullable<Restaurant['sourceMenus']>[number]

// Der Klick auf den Bericht soll nicht zugleich den Import des Eintrags auslösen.
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

const SourceMenuItem = React.memo(
	({
		sourceMenu,
		onImport,
		onShowReport,
	}: {
		sourceMenu: SourceMenu
		onImport: (source: string) => void
		onShowReport: (source: unknown) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(() => {
			onImport(sourceMenu.name)
		}, [onImport, sourceMenu.name])

		const isComplete = sourceMenu.hasPdf && sourceMenu.hasJson

		return (
			<MenuItem onClick={handleClick} disabled={!isComplete}>
				<ListItemText
					primary={`Stand ${new Date(sourceMenu.date).toLocaleDateString('de-DE')}`}
					secondary={
						isComplete
							? sourceMenu.menuVersion === null
								? sourceMenu.name
								: `${sourceMenu.name} · zuletzt importiert als v${sourceMenu.menuVersion}`
							: `${sourceMenu.name} · ${sourceMenu.hasPdf ? 'JSON' : 'PDF'} fehlt`
					}
				/>
				{isComplete ? (
					<Box sx={sxReport} onClick={stopPropagation}>
						<IconButton
							id={sourceMenu.name}
							iconName="FactCheck"
							toolTip="Import-Bericht ansehen"
							size="small"
							onClick={onShowReport}
						/>
					</Box>
				) : null}
			</MenuItem>
		)
	}
)

// Die Kartenstände eines Restaurants, wie sie in api/src/restaurantData liegen: <slug>-<YYYY-MM-DD>.pdf
// vom Restaurant und die daraus übersetzte .json. Jeder Stand lässt sich beliebig oft als neue
// Menü-Version importieren; ob sie als Preview online geht, entscheidet erst das Aktivieren.
const SourceMenuPicker = (): JSX.Element => {
	const { selectedRestaurant, handleImportSourceMenu } = useMenuActions()
	const sourceMenus = selectedRestaurant?.sourceMenus ?? []

	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
	const anchorRef = React.useRef<HTMLDivElement>(null)

	const handleOpen = React.useCallback(() => {
		setAnchorEl(anchorRef.current)
	}, [])

	const handleClose = React.useCallback(() => {
		setAnchorEl(null)
	}, [])

	const handleImport = React.useCallback(
		(source: string) => {
			setAnchorEl(null)
			handleImportSourceMenu(source)
		},
		[handleImportSourceMenu]
	)

	// IconButton reicht seine id, den Namen des Kartenstands, an onClick weiter.
	const handleShowReport = React.useCallback(
		(source: unknown) => {
			if (typeof source !== 'string') return
			setAnchorEl(null)
			handleImportSourceMenu(source, false)
		},
		[handleImportSourceMenu]
	)

	return (
		<Box ref={anchorRef}>
			<IconButton iconName="Add" toolTip="Neue Menü-Version importieren" onClick={handleOpen} />
			<Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={handleClose}>
				{sourceMenus.length === 0 ? (
					<MenuItem disabled>
						In api/src/restaurantData liegt noch keine Karte (&lt;slug&gt;-&lt;JJJJ-MM-TT&gt;.pdf).
					</MenuItem>
				) : (
					sourceMenus.map((sourceMenu) => (
						<SourceMenuItem
							key={sourceMenu.name}
							sourceMenu={sourceMenu}
							onImport={handleImport}
							onShowReport={handleShowReport}
						/>
					))
				)}
			</Menu>
		</Box>
	)
}

export default React.memo(SourceMenuPicker)
